/**
 * Menu Scope Selection Container
 * This component allows users to choose between store or organization menu scope
 *
 * @module containers/MenuScope
 */
import { withTranslation, Trans } from 'react-i18next';
import { compose } from 'redux';

// MUI Components
import Box from '@mui/material/Box';

// Custom Icons
import StoreIcon from '@experimental-components/IconsComponents/StoreIcon';
import OrganizationIcon from '@experimental-components/IconsComponents/OrganizationIcon';

// Layout Components
import Layout, { ContentLayout } from 'Layout';

// Components
import TitleSection from 'components/TitleSection';
import ScopeCard from 'components/ScopeCard';

// Auth & Providers
import { withRequiredLicense } from 'providers/root/AuthProvider';
import { MENU_SCOPE } from 'utils/menuMaker/constants';
import { useMenuScope } from 'hooks/menuScope';

// Styles
import { useTheme } from '@mui/material/styles';

// Local imports
import { MenuScopeProps } from './types';
import useStyles from './styles';
import MenuScopeSkeleton from './MenuScopeSkeleton';

/**
 * Menu Scope Selection Component
 * Allows users to choose between store (branch) or organization level menu scope
 * Uses unified navigation hook to manage scope selection and navigation state
 *
 * @param {MenuScopeProps} props - Component props
 * @param {boolean} props.permissionOrganizationAccess - Permission flag for organization level access
 * @param {Function} props.t - Translation function
 * @returns {React.ReactElement} Menu scope selection component
 *
 * @example
 * <MenuScope
 *   permissionOrganizationAccess={true}
 *   t={t}
 *   history={history}
 *   location={location}
 *   match={match}
 * />
 */
const MenuScope = ({ t, permissionOrganizationAccess }: MenuScopeProps): React.ReactElement => {
  const classes = useStyles();
  const theme = useTheme();

  // 🎯 MAIN HOOK: All menu scope functionality in one place
  const {
    isNewUser,
    hasOrganizationAccess,
    textKeys,
    sucursalLabel,
    handleScopeSelection,
    isLoadingMenus,
    canAccessStore,
  } = useMenuScope({
    permissionOrganizationAccess,
    autoRedirect: true,
    trackEvents: true,
  });

  // Show skeleton while organization access is being initialized or menus are loading
  // hasOrganizationAccess will be null until useMenuScopeEffects sets it based on permissions
  const isInitializing = hasOrganizationAccess === null || isLoadingMenus;

  return (
    <Layout>
      <ContentLayout>
        <ContentLayout.Container>
          {isInitializing ? (
            // Loading state - Show complete skeleton while initializing permissions or loading menus
            <MenuScopeSkeleton />
          ) : (
            // Loaded state - Show actual content
            <Box className={classes.container}>
              {/* Title and Subtitle */}
              <Box className={classes.headerSection}>
                <TitleSection
                  component="h1"
                  description={textKeys.subtitleKey ? <Trans i18nKey={textKeys.subtitleKey} /> : undefined}
                  descriptionProps={{
                    sx: {
                      fontSize: theme.typography.pxToRem(16),
                      lineHeight: theme.typography.pxToRem(24),
                      color: theme.palette.text.secondary,
                      textAlign: 'center',
                    },
                  }}
                  fontSize={theme.typography.pxToRem(34)}
                  fontWeight={theme.typography.fontWeightBold}
                  title={t(textKeys.titleKey)}
                  titleProps={{
                    sx: {
                      lineHeight: theme.typography.pxToRem(48),
                      margin: 0,
                      textAlign: 'center',
                      color: theme.palette.text.primary,
                    },
                  }}
                  wrapperProps={{
                    mx: 0,
                    px: 0,
                  }}
                />
              </Box>

              {/* Cards Container */}
              <Box className={classes.cardsContainer}>
                {/* Store/Branch Card */}
                <Box className={classes.cardWrapper}>
                  <ScopeCard
                    color="default"
                    description={<Trans i18nKey={textKeys.storeDescriptionKey} />}
                    disabled={!canAccessStore}
                    icon={<StoreIcon />}
                    onClick={() => handleScopeSelection(MENU_SCOPE.STORE)}
                    title={!sucursalLabel.includes(':') ? sucursalLabel : t(sucursalLabel)}
                  />
                </Box>

                {/* Organization Card */}
                <Box className={classes.cardWrapper}>
                  <ScopeCard
                    badgeText={t(textKeys.recommendedKey)}
                    color="primary"
                    description={<Trans i18nKey={textKeys.organizationDescriptionKey} />}
                    disabled={!hasOrganizationAccess}
                    icon={<OrganizationIcon />}
                    onClick={() => handleScopeSelection(MENU_SCOPE.ORGANIZATION)}
                    showBadge={isNewUser || hasOrganizationAccess}
                    title={t(textKeys.organizationKey)}
                  />

                  {/* Error message for organization card when no access */}
                  {!hasOrganizationAccess && textKeys.noPermissionKey && (
                    <Box className={classes.errorMessage}>
                      <Trans i18nKey={textKeys.noPermissionKey} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </ContentLayout.Container>
      </ContentLayout>
    </Layout>
  );
};

export default compose(withRequiredLicense(), withTranslation(['menuMaker', 'common']))(MenuScope);
